import { DashboardHeader } from "@/components/layout/header";
import { CalendarSection, type CalendarView } from "@/components/overview/calendar-section";
import { FilterBar } from "@/components/overview/filter-bar";
import { ProjectList } from "@/components/overview/project-list";
import { TasksRail } from "@/components/overview/tasks-rail";
import { TimeOffRail } from "@/components/overview/timeoff-rail";
import { TriageRail } from "@/components/overview/triage-rail";
import { WaitingRail } from "@/components/overview/waiting-rail";
import {
  type CalendarEvent,
  type Domain,
  type Projection,
  type TimeOffBlock,
  type TriageSummary,
  fetchEvents,
  fetchProjection,
  fetchTimeOff,
  fetchTriageSummary,
} from "@/lib/api";
import {
  DEFAULT_FILTERS,
  type Filters,
  addDays,
  dayRange,
  domainForAccount,
  eventItems,
  filterEvents,
  filterProjects,
  groupTasksByProject,
  groupWaitingByPerson,
  isoDay,
  itemsByDay,
  relativeAge,
  scopeProjects,
  sortProjects,
  tasksFor,
  vaultDateItems,
} from "@/lib/overview";
import { summarize as summarizeTimeOff, windowStartFor } from "@/lib/timeoff";
import { Box, Spinner, Text } from "@okshaun/components";
import { useEffect, useMemo, useReducer, useState } from "react";

const CALENDAR_URL = "https://cal.sfox.work";
const POLL_INTERVAL_MS = 60_000;
const CAL_VIEW_KEY = "overview.calendarView";

interface State {
  filters: Filters;
  selected: string | null;
}

type Action =
  | { type: "domain"; domain: Filters["domain"] }
  | { type: "toggle"; facet: "waiting" | "hasDate" | "touchedThisWeek" }
  | { type: "select"; slug: string }
  | { type: "clear" };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "domain":
      return { ...state, filters: { ...state.filters, domain: action.domain }, selected: null };
    case "toggle":
      return {
        ...state,
        filters: { ...state.filters, [action.facet]: !state.filters[action.facet] },
        selected: null,
      };
    case "select":
      return { ...state, selected: state.selected === action.slug ? null : action.slug };
    case "clear":
      return { ...state, selected: null };
  }
}

function readCalendarView(): CalendarView {
  try {
    return localStorage.getItem(CAL_VIEW_KEY) === "grid" ? "grid" : "strip";
  } catch {
    return "strip";
  }
}

export function OverviewPage() {
  const [{ filters, selected }, dispatch] = useReducer(reducer, {
    filters: DEFAULT_FILTERS,
    selected: null,
  });
  const [calView, setCalView] = useState<CalendarView>(readCalendarView);
  const [projection, setProjection] = useState<Projection | null>(null);
  const [projectionError, setProjectionError] = useState<string | null>(null);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [timeOff, setTimeOff] = useState<TimeOffBlock[] | null>(null);
  const [timeOffLoading, setTimeOffLoading] = useState(true);
  const [timeOffError, setTimeOffError] = useState<string | null>(null);
  const [triage, setTriage] = useState<TriageSummary | null>(null);

  const today = isoDay(new Date());
  const days = useMemo(() => dayRange(today, 14), [today]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchProjection();
        if (!cancelled) {
          setProjection(data);
          setProjectionError(null);
        }
      } catch (err) {
        if (!cancelled) setProjectionError(err instanceof Error ? err.message : "Failed to load");
      }
    }
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchEvents(days[0], days[days.length - 1]);
        if (!cancelled) {
          setEvents(data);
          setEventsError(null);
        }
      } catch (err) {
        if (!cancelled) setEventsError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        if (!cancelled) setEventsLoading(false);
      }
    }
    load();
    const id = setInterval(load, POLL_INTERVAL_MS * 5);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [days]);

  // Triage summary: pending task IDs and label counts. Optional — the page works without it.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchTriageSummary();
        if (!cancelled) setTriage(data);
      } catch {
        if (!cancelled) setTriage(null);
      }
    }
    load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
  const pendingIds = useMemo(() => new Set(triage?.pending ?? []), [triage]);

  // Time off: rolling 12 months back plus the next 6 months of bookings, every 15 minutes.
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const data = await fetchTimeOff(windowStartFor(today), addDays(today, 183));
        if (!cancelled) {
          setTimeOff(data);
          setTimeOffError(null);
        }
      } catch (err) {
        if (!cancelled) setTimeOffError(err instanceof Error ? err.message : "Failed to load");
      } finally {
        if (!cancelled) setTimeOffLoading(false);
      }
    }
    load();
    const id = setInterval(load, POLL_INTERVAL_MS * 15);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [today]);

  const onView = (view: CalendarView) => {
    setCalView(view);
    try {
      localStorage.setItem(CAL_VIEW_KEY, view);
    } catch {
      // per-viewer convenience only
    }
  };

  const allProjects = projection?.projects ?? [];
  const filtered = useMemo(
    () => sortProjects(filterProjects(allProjects, filters, today)),
    [allProjects, filters, today]
  );
  const scoped = useMemo(() => scopeProjects(filtered, selected), [filtered, selected]);
  const scopedProject = selected ? (scoped[0] ?? null) : null;
  const scopedTasks = useMemo(
    () => tasksFor(projection?.tasks ?? [], scoped),
    [projection, scoped]
  );
  const taskGroups = useMemo(() => groupTasksByProject(scopedTasks, scoped), [scopedTasks, scoped]);
  const readyCount = scopedTasks.filter((t) => !t.waiting).length;
  const waitingGroups = useMemo(() => groupWaitingByPerson(scopedTasks), [scopedTasks]);
  const waitingCount = scopedTasks.length - readyCount;

  const byDay = useMemo(() => {
    const filteredTasks = tasksFor(projection?.tasks ?? [], filtered);
    const items = [
      ...eventItems(filterEvents(events, filters), days),
      ...vaultDateItems(filteredTasks, filtered, days),
    ];
    return itemsByDay(items, days);
  }, [events, filters, projection, filtered, days]);

  const timeOffSummary = useMemo(
    () => (timeOff ? summarizeTimeOff(timeOff, today) : null),
    [timeOff, today]
  );

  const personalConnected = events.some((e) => domainForAccount(e.accountEmail) === "personal");

  const counts = useMemo(() => {
    const byDomain: Record<Domain, number> = { cetec: 0, mkbd: 0, personal: 0 };
    for (const p of allProjects) byDomain[p.domain]++;
    return { all: allProjects.length, byDomain };
  }, [allProjects]);

  return (
    <Box
      display="flex"
      flexDir="column"
      gap="32"
      pt="12"
      pb="20"
      px="20"
      maxW="[1600px]"
      mx="auto"
      minH="screen"
    >
      <DashboardHeader
        trailing={
          projection ? (
            <Text textStyle="mono.xs" color="text.subtlest">
              bee read the vault {relativeAge(projection.generatedAt)}
            </Text>
          ) : undefined
        }
      />

      <FilterBar
        filters={filters}
        counts={counts}
        onDomain={(domain) => dispatch({ type: "domain", domain })}
        onToggle={(facet) => dispatch({ type: "toggle", facet })}
      />

      <CalendarSection
        view={calView}
        onView={onView}
        days={days}
        today={today}
        byDay={byDay}
        calendarUrl={CALENDAR_URL}
        personalConnected={personalConnected}
        loading={eventsLoading}
        error={eventsError}
      />

      {!projection && !projectionError && (
        <Box display="flex" alignItems="center" gap="4">
          <Spinner size="sm" />
          <Text textStyle="mono.xs">Loading the vault…</Text>
        </Box>
      )}
      {projectionError && (
        <Text textStyle="mono.sm" color="text.danger">
          vault: {projectionError}
        </Text>
      )}

      {projection && (
        <Box
          display="grid"
          gridTemplateColumns="[minmax(0, 2fr) minmax(0, 1fr)]"
          gap="24"
          alignItems="start"
        >
          <ProjectList
            projects={filtered}
            total={allProjects.length}
            selected={selected}
            onSelect={(slug) => dispatch({ type: "select", slug })}
          />
          <Box display="flex" flexDir="column" gap="32" minW="0">
            <TimeOffRail summary={timeOffSummary} loading={timeOffLoading} error={timeOffError} />
            <TasksRail
              groups={taskGroups}
              readyCount={readyCount}
              waitingCount={waitingCount}
              scoped={scopedProject}
              onClear={() => dispatch({ type: "clear" })}
              pendingIds={pendingIds}
              triageCount={triage?.counts.labeled}
            />
            <WaitingRail groups={waitingGroups} total={waitingCount} scoped={scopedProject} />
            <TriageRail items={projection.unsorted} />
          </Box>
        </Box>
      )}
    </Box>
  );
}
